import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import { Checkbox } from '@components/Checkbox';
import { Switch } from '@components/Switch';
import { MultiSelectLabels } from '@components/MultiSelectLabels';
import { LabelType } from '@constants/labels';
import { useTranslation } from '@hooks';
import { StyledSwitch } from './styles';

const PlatformWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    transform: 'translateX(-7px)',
    '& .MuiCheckbox-root': {
        padding: 4,
    },
});

const FieldWrapper = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: 16,
    marginTop: '8px',
    alignItems: 'center',
});

const Subscription = ({ isView }) => {
    const { data: labels } = useSelector(state => state.labels);
    const { t } = useTranslation();
    const { control, setValue, watch, getValues } = useFormContext();

    const subscriptionEnable = watch('subscriptionEnabled');
    const emailField = watch('subscriptionEmailEnabled');
    const phoneField = watch('subscriptionPhoneEnabled');
    const nameField = watch('subscriptionNameEnabled');

    const labelsOptions = useMemo(() => labels.map(item => ({ value: item.id, label: item.name })), [labels]);

    return (
        <>
            <Typography variant="subtitle1">{t('MicrositesPage.subscriptionTitle')}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', margin: '4px 0 0' }}>
                {t('MicrositesPage.subscriptionSubtitle')}
            </Typography>
            <Controller
                control={control}
                name="subscriptionEnabled"
                render={({ field, ref }) => (
                    <StyledSwitch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('subscriptionEnabled', event.target.checked, {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                            if (!event.target.checked) {
                                setValue('subscriptionEmailEnabled', false, { shouldValidate: true });
                                setValue('subscriptionEmailRequired', false, { shouldValidate: true });
                                setValue('subscriptionPhoneEnabled', false, { shouldValidate: true });
                                setValue('subscriptionPhoneRequired', false, { shouldValidate: true });
                                setValue('subscriptionNameEnabled', false);
                                setValue('subscriptionNameRequired', false);
                                setValue('subscriptionLabels', []);
                            } else {
                                setValue('subscriptionEmailEnabled', true, { shouldValidate: true });
                                setValue('subscriptionEmailRequired', true, { shouldValidate: true });
                            }
                        }}
                        label={t('MicrositesPage.enableSubscription')}
                        disabled={isView}
                    />
                )}
            />
            {subscriptionEnable && (
                <>
                    <FieldWrapper>
                        <Controller
                            control={control}
                            name="subscriptionEmailEnabled"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('subscriptionEmailEnabled')}
                                    handleChange={event => {
                                        setValue('subscriptionEmailEnabled', event.target.checked, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                        if (!event.target.checked) {
                                            setValue('subscriptionEmailRequired', false, {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                        }
                                    }}
                                    label={t('FeedbackSurveys.contactEmail')}
                                    disabled={isView}
                                />
                            )}
                        />
                        {emailField ? (
                            <PlatformWrapper>
                                <Controller
                                    control={control}
                                    name="subscriptionEmailRequired"
                                    render={({ field, ref }) => (
                                        <Checkbox
                                            ref={ref}
                                            disabled={isView}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                            onChange={e => {
                                                setValue('subscriptionEmailRequired', e.target.checked, {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                });
                                            }}
                                            checked={field.value}
                                        />
                                    )}
                                />
                                <Typography variant="body2">{t('common.required')}</Typography>
                            </PlatformWrapper>
                        ) : (
                            <div />
                        )}
                    </FieldWrapper>
                    <FieldWrapper>
                        <Controller
                            control={control}
                            name="subscriptionPhoneEnabled"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('subscriptionPhoneEnabled')}
                                    handleChange={event => {
                                        setValue('subscriptionPhoneEnabled', event.target.checked, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                        if (!event.target.checked) {
                                            setValue('subscriptionPhoneRequired', false, {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                        }
                                    }}
                                    label={t('FeedbackSurveys.contactPhone')}
                                    disabled={isView}
                                />
                            )}
                        />
                        {phoneField ? (
                            <PlatformWrapper>
                                <Controller
                                    control={control}
                                    name="subscriptionPhoneRequired"
                                    render={({ field, ref }) => (
                                        <Checkbox
                                            ref={ref}
                                            disabled={isView}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                            onChange={e => {
                                                setValue('subscriptionPhoneRequired', e.target.checked, {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                });
                                            }}
                                            checked={field.value}
                                        />
                                    )}
                                />
                                <Typography variant="body2">{t('common.required')}</Typography>
                            </PlatformWrapper>
                        ) : (
                            <div />
                        )}
                    </FieldWrapper>
                    <FieldWrapper style={{ marginBottom: '16px' }}>
                        <Controller
                            control={control}
                            name="subscriptionNameEnabled"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('subscriptionNameEnabled')}
                                    handleChange={event => {
                                        setValue('subscriptionNameEnabled', event.target.checked, {
                                            shouldDirty: true,
                                        });
                                        if (!event.target.checked) {
                                            setValue('subscriptionNameRequired', false, { shouldDirty: true });
                                        }
                                    }}
                                    label={t('FeedbackSurveys.contactName')}
                                    disabled={isView}
                                />
                            )}
                        />
                        {nameField ? (
                            <PlatformWrapper>
                                <Controller
                                    control={control}
                                    name="subscriptionNameRequired"
                                    render={({ field, ref }) => (
                                        <Checkbox
                                            ref={ref}
                                            disabled={isView}
                                            inputProps={{ 'aria-label': 'controlled' }}
                                            onChange={e => {
                                                setValue('subscriptionNameRequired', e.target.checked, {
                                                    shouldDirty: true,
                                                });
                                            }}
                                            checked={field.value}
                                        />
                                    )}
                                />
                                <Typography variant="body2">{t('common.required')}</Typography>
                            </PlatformWrapper>
                        ) : (
                            <div />
                        )}
                    </FieldWrapper>
                    <Controller
                        control={control}
                        name="subscriptionLabels"
                        render={({ field, ref }) => (
                            <MultiSelectLabels
                                {...field}
                                ref={ref}
                                onChange={value => {
                                    setValue('subscriptionLabels', value, { shouldDirty: true });
                                }}
                                options={labelsOptions}
                                label={t('common.labels')}
                                fullWidth
                                disabled={isView}
                                type={LabelType.Contact}
                                onCreateCallback={newLabel => {
                                    const values = getValues();
                                    setValue('subscriptionLabels', [...values.subscriptionLabels, newLabel], {
                                        shouldDirty: true,
                                    });
                                }}
                            />
                        )}
                    />
                </>
            )}
        </>
    );
};

export default Subscription;
